import axios from 'axios';

// ── API base URL ──────────────────────────────────────────────────────────────
// REACT_APP_API_URL must be set in Netlify environment variables
// Example: https://adm-backend-production.up.railway.app
const BASE = process.env.REACT_APP_API_URL || '';

if (!BASE) {
  console.error(
    '❌ REACT_APP_API_URL is not set in Netlify environment variables.\n' +
    'Add it as: https://your-backend.railway.app\n' +
    'Payments and AI will not work until this is set.'
  );
}

export const api = axios.create({
  baseURL:  BASE,
  timeout:  30000,
  headers:  { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('adm_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;

  // Debug: log every API call in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`API → ${cfg.method?.toUpperCase()} ${cfg.baseURL}${cfg.url}`);
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r.data,
  (err) => {
    const status = err.response?.status;
    const url    = err.config?.url;

    if (status === 404) {
      console.error(
        `❌ 404 on ${url} — Check that:\n` +
        `1. REACT_APP_API_URL in Netlify is set to your Railway URL\n` +
        `2. Your Railway backend is running (check Railway dashboard)\n` +
        `3. The route exists: visit ${BASE}${url} in your browser`
      );
    }
    if (status === 503) {
      console.error(`❌ 503 on ${url} — Backend service offline or Stripe not configured`);
    }

    return Promise.reject(err.response?.data || err);
  }
);

// ── Endpoints ─────────────────────────────────────────────────────────────────
export const aiPredict        = (data) => api.post('/api/ai/predict', data);
export const aiAnalyze        = (data) => api.post('/api/ai/analyze', data);
export const aiChat           = (data) => api.post('/api/ai/chat', data);
export const getAIPredictions = ()     => api.get('/api/ai/predictions');
export const getAIThreats     = ()     => api.get('/api/ai/threats');
export const getAIStatus      = ()     => api.get('/api/ai/status');
export const getLiveFeed      = ()     => api.get('/api/sightings/live');
export const getPlans         = ()     => api.get('/api/payments/plans');
export const subscribeSMS     = (data) => api.post('/api/alerts/subscribe', data);
export const testSMS          = (data) => api.post('/api/alerts/test', data);
export const getSMSStatus     = ()     => api.get('/api/alerts/status');
export const getAlertZones    = (email)=> api.get(`/api/alerts/zones?email=${encodeURIComponent(email)}`);
export const createAlertZone  = (data) => api.post('/api/alerts/zones', data);
export const deleteAlertZone  = (id, email) => api.delete(`/api/alerts/zones/${id}`, { data:{ email } });
export const getApiKey        = (data) => api.post('/api/v1/keys/create', data);
export const queryAPI         = (endpoint, key) => axios.get(`${BASE}/api/v1/${endpoint}`, { headers:{ 'X-API-Key':key } });
export const getSightingStats = ()     => api.get('/api/sightings/stats/summary');
export const getCognitionState= ()     => api.get('/api/cognition/state');
export const getCognitionFocus= ()     => api.get('/api/cognition/focus');
export const getSimulation    = ()     => api.get('/api/cognition/simulation');
export const verifySession    = (data) => api.post('/api/payments/verify-session', data);
export const createCheckout   = (data) => api.post('/api/payments/checkout', data);
export const createDonation   = (data) => api.post('/api/payments/donate', data);
export const checkStripe      = ()     => api.get('/api/payments/status');
